import { useState } from 'react';
import { Input } from '../../../../../components/forms/Input';
import { Select } from '../../../../../components/forms/Select';
import { maskCEP } from '../../../../../utils/mask';
import { UF_OPTIONS } from '../../constants';
import { fetchAddressByCep } from '../../cep';
import styles from '../WizardShell/WizardShell.module.css';

interface IAddressFields {
  prefix: string;
  register: any;
  errors: any;
  setValue: any;
  setFocus?: any;
}

export function AddressFields({
  prefix,
  register,
  errors,
  setValue,
  setFocus,
}: IAddressFields) {
  const field = (name: string) => `${prefix}.${name}`;
  const errorOf = (name: string) => errors?.[name]?.message ?? '';
  const [isLookingUp, setIsLookingUp] = useState(false);

  /* Fills what the CEP knows and leaves everything editable — a city-wide CEP
     has no street or district, and a failed lookup must not block the form. */
  async function handleCepChange(rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 8);
    setValue(field('zipCode'), maskCEP(digits), { shouldValidate: true });

    if (digits.length !== 8) return;

    setIsLookingUp(true);
    const address = await fetchAddressByCep(digits);
    setIsLookingUp(false);

    if (!address) return;

    const fill = (name: string, value: string) => {
      if (value) setValue(field(name), value, { shouldValidate: true });
    };

    fill('street', address.street);
    fill('district', address.district);
    fill('city', address.city);
    fill('state', address.state);
    setFocus?.(field('number'));
  }

  return (
    <>
      <div className={styles.field}>
        <Input
          type="text"
          label="CEP"
          placeholder="00000-000"
          inputMode="numeric"
          maxLength={9}
          autoComplete="off"
          errors={errorOf('zipCode')}
          {...register(field('zipCode'), {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              handleCepChange(event.target.value),
          })}
        />
        {isLookingUp && <p className={styles.hint}>Buscando endereço...</p>}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <Select
            label="Estado"
            placeholder="Selecione o estado"
            options={UF_OPTIONS}
            errors={errorOf('state')}
            {...register(field('state'))}
          />
        </div>
        <div className={styles.field}>
          <Input
            type="text"
            label="Cidade"
            placeholder="Cidade"
            autoComplete="off"
            errors={errorOf('city')}
            {...register(field('city'))}
          />
        </div>
      </div>

      <div className={styles.field}>
        <Input
          type="text"
          label="Bairro"
          placeholder="Bairro"
          autoComplete="off"
          errors={errorOf('district')}
          {...register(field('district'))}
        />
      </div>

      <div className={styles.field}>
        <Input
          type="text"
          label="Logradouro"
          placeholder="Rua, avenida..."
          autoComplete="off"
          errors={errorOf('street')}
          {...register(field('street'))}
        />
      </div>

      <div className={styles.field}>
        <Input
          type="text"
          label="Número"
          placeholder="Número"
          autoComplete="off"
          errors={errorOf('number')}
          {...register(field('number'))}
        />
      </div>
    </>
  );
}
